import { usePaintBlockPointsLazyQuery } from '@/__generated__/graphql';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useIsolateCustomBlock } from '../../hooks';
import * as localState from '../../state';
import * as state from '@/components/Analysis/state';
import { useAtom, useAtomValue } from 'jotai';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { UnitSystemEnum } from '@/__generated__/graphql';
import { SQFEET_TO_SQMETERS_FACTOR } from '@/utils/unitSystem';
import { prismToAnalysisCuboid } from '@/utils/geometry';
import { AnalysisCustomBlock } from '@/types';
import { extractErrorMessageFromUnknownError } from '@/utils/error';

type Props = {
  block: AnalysisCustomBlock;
};

export const useCustomBlockListItemData = ({ block }: Props) => {
  const [fetchPaintRegionPoints, { loading: loadingPaintRegion }] = usePaintBlockPointsLazyQuery({
    fetchPolicy: 'cache-first',
  });
  const [customBlockCuboids, setCustomBlockCuboids] = useAtom(localState.customBlockCuboids);
  const customBlocksQueryInput = useAtomValue(localState.customBlocksQueryInput);
  const hoveredCustomBlockId = useAtomValue(localState.hoveredCustomBlockId);
  const { showSnackBar } = useSnackBarMessage({});
  const { isolateCustomBlock } = useIsolateCustomBlock({});

  const [hasInvalidPrism, setHasInvalidPrism] = useState(false);

  const unitSystem = useAtomValue(state.unitSystem);

  const unitSuffix = unitSystem === UnitSystemEnum.Imperial ? 'ft²' : 'm²';

  // Since data in the DB, was calculated in metre square, we need to convert it to feet square
  const multiplicationFactor =
    unitSystem === UnitSystemEnum.Imperial ? SQFEET_TO_SQMETERS_FACTOR : 1;

  const onChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      try {
        // If the incoming value is false, the checkbox is being unchecked
        // so in that case remove the current cuboid from the list
        // doing so removes it from the 3D viewer
        const value = event.target.checked;
        if (!value) {
          setCustomBlockCuboids((current) => {
            const copy = current.asMutableStateMap();
            copy.delete(block.paintRegionId);
            return copy.asStateMap();
          });
          return;
        }

        const paintRegion = await fetchPaintRegionPoints({
          variables: {
            paintRegionId: block.paintRegionId,
          },
          // A block's 3D data NEVER changes so we can safely cache it
          fetchPolicy: 'cache-first',
        });
        const prism = paintRegion?.data?.paintRegion?.volume?.inclusion;
        if (!prism || !prism?.z_range || !prism?.xy_polygon) {
          setHasInvalidPrism(true);
          showSnackBar('Missing 3D data. Please contact support', 'error');
          return;
        }

        const cuboid = prismToAnalysisCuboid({
          prismData: prism,
          id: block.paintRegionId,
          name: block.name,
        });
        setCustomBlockCuboids((current) => {
          const copy = current.asMutableStateMap();
          copy.set(block.paintRegionId, cuboid);
          return copy.asStateMap();
        });
      } catch (error) {
        const message = extractErrorMessageFromUnknownError(error);
        showSnackBar(message, 'error');
        setHasInvalidPrism(true);
      }
    },
    [block.paintRegionId, fetchPaintRegionPoints, setCustomBlockCuboids, showSnackBar, block?.name]
  );

  const onClick = useCallback(async () => {
    try {
      const paintRegion = await fetchPaintRegionPoints({
        variables: {
          paintRegionId: block.paintRegionId,
        },
        fetchPolicy: 'cache-first',
      });
      const prism = paintRegion?.data?.paintRegion?.volume?.inclusion;
      const files = paintRegion?.data?.paintRegion?.files || undefined;
      if (!prism || !prism?.z_range || !prism?.xy_polygon) {
        showSnackBar('Missing 3D data. Please contact support', 'error');
        return;
      }

      isolateCustomBlock(block, prism, files);
    } catch (error) {
      const message = extractErrorMessageFromUnknownError(error);
      showSnackBar(message, 'error');
      setHasInvalidPrism(true);
    }
  }, [block, fetchPaintRegionPoints, isolateCustomBlock, showSnackBar]);

  const selectedDefectValue = useMemo(() => {
    // First check if the selected field is a POI type by looking in totalCountByPoiType
    const poiValue = block.totalCountByPoiType?.find(
      (item) => item?.type === customBlocksQueryInput.sortBy
    )?.value;
    if (poiValue !== undefined) {
      if (typeof poiValue !== 'number') return undefined;
      if (Number.isNaN(poiValue)) return undefined;
      // For POI data, return whole numbers (no decimal places)
      return Math.round(poiValue);
    }

    // If not found in POI data, use existing flow for paint block fields
    const value = block.totalArea?.find(
      (item) => item?.type === customBlocksQueryInput.sortBy
    )?.value;
    if (value === undefined) return undefined;
    if (typeof value !== 'number') return undefined;
    if (Number.isNaN(value)) return undefined;
    return Number.parseFloat(value.toFixed(2)) * multiplicationFactor;
  }, [
    block.totalArea,
    block.totalCountByPoiType,
    multiplicationFactor,
    customBlocksQueryInput.sortBy,
  ]);
  const area = useMemo(() => {
    const value = block.totalArea?.find((item) => item?.type === 'area')?.value;
    if (value === undefined) return undefined;
    if (typeof value !== 'number') return undefined;
    if (Number.isNaN(value)) return undefined;
    return Number.parseFloat(value.toFixed(2)) * multiplicationFactor;
  }, [block.totalArea, multiplicationFactor]);

  const isSelected = useMemo(
    () => customBlockCuboids.has(block.paintRegionId),
    [block.paintRegionId, customBlockCuboids]
  );

  const isHovered = useMemo(
    () => hoveredCustomBlockId === block.paintRegionId,
    [block.paintRegionId, hoveredCustomBlockId]
  );

  return {
    onChange,
    onClick,
    selectedDefectValue,
    area,
    loadingPaintRegion,
    hasInvalidPrism,
    unitSuffix,
    isSelected,
    isHovered,
  };
};
